/**
 * Admin → Contract Setup → Company & Accreditations
 *
 * Edits company registration + accreditation numbers used in:
 *  - Contract pack PDF header (logo conditionally shown when reg number set)
 *  - Contract pack PDF footer (accreditation strip on every page)
 */
import React, { useEffect, useState } from 'react';
import { Building2, Save, Loader2, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';

const FIELDS = [
  { key: 'company_name', label: 'Company name', hint: 'Used in PDF footer strip' },
  { key: 'company_registration', label: 'Company registration number', hint: 'Companies House registration' },
  { key: 'mcs_number', label: 'MCS certification number', hint: 'Leave blank to HIDE the MCS logo from the header' },
  { key: 'hies_number', label: 'HIES / Consumer Code registration', hint: 'Leave blank to HIDE the HIES logo' },
  { key: 'niceic_number', label: 'NICEIC registration', hint: 'Leave blank to HIDE the NICEIC logo' },
  { key: 'chas_number', label: 'CHAS Elite ID', hint: 'Leave blank to HIDE the CHAS logo' },
];

export const CompanyAccreditationsContent = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    let mounted = true;
    api.get('/contract-accreditations')
      .then(({ data }) => { if (mounted) setForm(data || {}); })
      .catch(() => toast.error('Failed to load company accreditations'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const handleChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: form[f.key] ?? '' }), {});
      const { data } = await api.put('/contract-accreditations', payload);
      setForm(data);
      toast.success('Company & accreditation details saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="accreds-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6 max-w-4xl mx-auto'} data-testid="company-accreditations-page">
      {!embedded && (
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-6">
          <Building2 className="w-6 h-6 text-amber-600" />
          Company & Accreditations
        </h1>
      )}

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-6 flex items-start gap-2" data-testid="accreds-info">
        <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-900 leading-relaxed">
          These details appear on every page of the generated contract pack:
          accreditation logos are <strong>only shown in the header</strong> when their registration
          number is set below — leave a number blank to hide the corresponding logo.
          The full strip is also rendered in the footer.
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Helios company details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={`accred-${f.key}`} className="text-xs font-semibold text-slate-700">
                {f.label}
              </Label>
              <Input
                id={`accred-${f.key}`}
                data-testid={`accred-${f.key}`}
                value={form[f.key] ?? ''}
                onChange={(e) => handleChange(f.key, e.target.value)}
                placeholder="—"
              />
              {f.hint && <p className="text-[11px] text-slate-500">{f.hint}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="accreds-save-btn"
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default function CompanyAccreditationsPage() {
  return <CompanyAccreditationsContent />;
}
